import { useRef, useState, useEffect, useCallback } from 'react';
import { useDrag, useDrop } from 'react-dnd';

import _ from 'lodash';
import dayjs from 'dayjs';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { grey, green, red, amber, blue } from '@mui/material/colors';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpRightFromSquare, faUser, faPlane, faShip, faTruck, faLock } from '@fortawesome/pro-light-svg-icons';

import { opportunitySellAndCost } from '../../utils/opportunityFinancials.js';
import CurrencyToText from '../../CurrencyToText.js';

const DRAG_TYPE = 'BOOKING_CARD';

const STAGE_COLORS = {
    'Quote': '#6366f1',
    'Booking': '#3b82f6',
    'Voyage': '#0891b2',
    'Invoicing': '#d97706',
    'Closed': '#475569',
};

function BookingsKanban(props) {
    const { opportunities, accounts, onOpen, onUpdateStage, org, exchangeRates, stages } = props;
    const [saving, setSaving] = useState({});
    const kanbanRef = useRef(null);

    useEffect(() => { kanbanRef.current?.focus(); }, []);

    const onKanbanKeyDown = useCallback((e) => {
        if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
            e.preventDefault();
            kanbanRef.current?.scrollBy({ left: e.key === 'ArrowRight' ? 300 : -300, behavior: 'smooth' });
        }
    }, []);

    function getBookingStage(opportunity) {
        const tag = (opportunity.tags || []).find(t => t.label?.startsWith('Booking:'));
        return tag ? tag.label.replace('Booking: ', '').replace('Booking:', '') : 'Quote';
    }

    async function onDrop({ opportunity, stage }) {
        const currentStage = getBookingStage(opportunity);
        if (currentStage === stage) return;
        setSaving(s => ({ ...s, [opportunity.id]: 'working' }));
        await onUpdateStage({ opportunity, stage });
        setSaving(s => ({ ...s, [opportunity.id]: 'success' }));
        setTimeout(() => setSaving(s => ({ ...s, [opportunity.id]: null })), 2000);
    }

    return (
        <Box ref={kanbanRef} tabIndex={0} onKeyDown={onKanbanKeyDown} sx={{ display: 'flex', mt: '12px', overflowX: 'auto', pb: '12px', outline: 'none' }}>
            {stages.map((stage, index) => {
                const isLocked = stage === 'Closed';
                const cards = opportunities.filter(o => getBookingStage(o) === stage);
                return (
                    <Column
                        key={stage}
                        stage={stage}
                        index={index}
                        cards={cards}
                        accounts={accounts}
                        onOpen={onOpen}
                        onDrop={onDrop}
                        saving={saving}
                        org={org}
                        exchangeRates={exchangeRates}
                        isLocked={isLocked}
                        color={STAGE_COLORS[stage] || grey[600]}
                    />
                );
            })}
        </Box>
    );
}

function Column({ stage, index, cards, accounts, onOpen, onDrop, saving, org, exchangeRates, isLocked, color }) {
    const [, drop] = useDrop({
        accept: DRAG_TYPE,
        drop: (item) => {
            onDrop({ opportunity: item.opportunity, stage });
        },
        canDrop: () => !isLocked,
    });

    return (
        <Box ref={drop} sx={{ flex: '0 0 280px', minWidth: '280px', maxWidth: '280px', mr: '6px' }}>
            <Box sx={{ background: color, borderRadius: '8px', padding: '8px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#fff' }}>{stage}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ fontSize: '11px', fontWeight: 700, color: 'rgba(255,255,255,0.7)', backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: '10px', px: '8px', py: '1px' }}>
                        {cards.length}
                    </Typography>
                    {isLocked && (
                        <Tooltip title='This column is read-only' arrow placement='top'>
                            <span><FontAwesomeIcon icon={faLock} style={{ fontSize: '11px', color: 'rgba(255,255,255,0.6)' }} /></span>
                        </Tooltip>
                    )}
                </Box>
            </Box>
            {cards.map(opportunity => (
                <Card
                    key={opportunity.id}
                    opportunity={opportunity}
                    accounts={accounts}
                    onOpen={onOpen}
                    saving={saving[opportunity.id]}
                    org={org}
                    exchangeRates={exchangeRates}
                    isLocked={isLocked}
                />
            ))}
        </Box>
    );
}

function Card({ opportunity, accounts, onOpen, saving, org, exchangeRates, isLocked }) {
    const _opportunity = useRef(opportunity);
    _opportunity.current = opportunity;
    const [isDragging, setIsDragging] = useState(false);
    const _setIsDragging = useRef(setIsDragging);
    _setIsDragging.current = setIsDragging;

    const [, drag] = useDrag(() => ({
        type: DRAG_TYPE,
        item: () => {
            _setIsDragging.current(true);
            return { opportunity: _opportunity.current };
        },
        collect: (monitor) => ({ isDragging: !!monitor.isDragging() }),
        end: () => { _setIsDragging.current(false); },
        canDrag: () => !isLocked,
    }));

    const account = (accounts || []).find(a => a && a.id === opportunity.account?.id);
    const accountName = account?.name || opportunity.account?.email || opportunity.account?.name || 'No account';

    const routes = (() => {
        if (opportunity.type === 'one-off' || opportunity.type === 'one-off-email' || opportunity.type === 'one-off-free-text') return opportunity.routes;
        if (opportunity.type === 'contract') return _.uniqBy((opportunity.offers || []).map(o => o.routes?.[0]).filter(Boolean), o => JSON.stringify(o));
        return [];
    })();
    const firstRoute = routes?.[0];

    const transportIcon = opportunity.transportationMethod === 'air' ? faPlane : opportunity.transportationMethod === 'land' ? faTruck : faShip;
    const ownerShort = (opportunity.owner || '').split('@')[0];

    // Profit
    const profitData = (() => {
        if (!exchangeRates || !org) return null;
        const cur = org.defaults?.currency || 'USD';
        // The quote's own Sell and Cost (opportunitySellAndCost), as on its page.
        const fin = opportunitySellAndCost({ opportunity, exchangeRates, organization: org });
        if (!fin) return null;
        try {
            const { sell, cost, gp } = fin;
            const margin = sell > 0 ? (gp / sell) * 100 : 0;
            if (sell === 0 && cost === 0) return null;
            if (!isFinite(sell) || !isFinite(cost)) return null;
            const fmt = new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
            return { sell, cost, gp, margin, cur, fmt };
        } catch (e) { return null; }
    })();

    const statusColor = (() => {
        if (opportunity.status === 'completed' || opportunity.status === 'endcustomer-accepted') return { bg: green[50], color: green[800] };
        if (opportunity.status === 'lost' || opportunity.status === 'endcustomer-rejected') return { bg: red[50], color: red[800] };
        return { bg: blue[50], color: blue[800] };
    })();

    return (
        <Paper
            ref={drag}
            elevation={0}
            onClick={() => onOpen({ opportunity })}
            sx={{
                mt: 1, p: 1.5, width: '278px', borderRadius: '8px',
                border: '1px solid #eee',
                cursor: isLocked ? 'default' : 'pointer',
                opacity: isLocked ? 0.7 : isDragging ? 0.3 : 1,
                '&:hover': { boxShadow: '0 1px 4px rgba(0,0,0,0.1)' },
                ...(opportunity.status === 'completed' || opportunity.status === 'endcustomer-accepted' ? { borderLeft: `3px solid ${green[500]}` } : {}),
                ...(opportunity.status === 'lost' || opportunity.status === 'endcustomer-rejected' ? { borderLeft: `3px solid ${red[400]}` } : {}),
            }}
        >
            {/* Account name */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.5 }}>
                <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#334155', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {accountName}
                </Typography>
                <IconButton size='small' onClick={(e) => { e.stopPropagation(); onOpen({ opportunity }); }} sx={{ color: grey[400], '&:hover': { color: grey[700] }, ml: 0.5 }}>
                    <FontAwesomeIcon icon={faArrowUpRightFromSquare} style={{ fontSize: '10px' }} />
                </IconButton>
            </Box>

            {/* Route */}
            {firstRoute && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                    <FontAwesomeIcon icon={transportIcon} style={{ fontSize: '10px', color: '#94a3b8' }} />
                    <Typography sx={{ fontSize: '11px', color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {firstRoute.source?.text?.split(' - ')[0] || ''} → {firstRoute.destination?.text?.split(' - ')[0] || ''}
                    </Typography>
                </Box>
            )}

            {/* Profit */}
            {profitData && (
                <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                    <Typography sx={{ fontSize: '10px', color: profitData.gp >= 0 ? green[700] : red[600], fontWeight: 600 }}>
                        GP: <CurrencyToText currency={profitData.cur} short={true}>{profitData.fmt.format(profitData.gp)}</CurrencyToText> ({profitData.margin.toFixed(1)}%)
                    </Typography>
                </Box>
            )}

            {/* Owner */}
            {ownerShort && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                    <FontAwesomeIcon icon={faUser} style={{ fontSize: '9px', color: '#94a3b8' }} />
                    <Typography sx={{ fontSize: '10px', color: '#94a3b8' }}>{ownerShort}</Typography>
                </Box>
            )}

            {/* Saving indicator */}
            {saving === 'working' && (
                <Typography variant='caption' sx={{ color: amber[700], fontSize: '10px' }}>Saving...</Typography>
            )}
            {saving === 'success' && (
                <Typography variant='caption' sx={{ color: green[600], fontSize: '10px' }}>Saved</Typography>
            )}
        </Paper>
    );
}

export default BookingsKanban;
